import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { recordPageVisit } from "@/lib/api";
import { 
  Heart, 
  Home, 
  Users, 
  Pill, 
  Clock,
  MessageCircle,
  CheckCircle,
  ArrowRight
} from "lucide-react";

const hospiceTopics = [
  {
    title: "What is Hospice Care?",
    content: `Hospice care is a type of health care that focuses on comfort and quality of life for people with a terminal illness. Rather than trying to cure the illness, hospice care aims to provide comfort, dignity, and support to both patients and their families.

Key aspects of hospice care include:
• Focus on comfort rather than cure
• Care provided wherever the patient calls home
• Support for the entire family, not just the patient
• A team approach involving doctors, nurses, social workers, chaplains, and volunteers
• Available 24 hours a day, 7 days a week`
  },
  {
    title: "Who is Eligible for Hospice?",
    content: `Hospice care is available to anyone with a terminal illness who has a life expectancy of six months or less if the disease runs its normal course. Eligibility includes:

• A physician's certification that the patient has a terminal illness with a prognosis of six months or less
• The patient (or family if the patient cannot) agrees to comfort-focused care rather than curative treatment
• The patient can be recertified for additional periods if they continue to meet the criteria

Important: Choosing hospice does not mean giving up hope. Many patients actually live longer with hospice care due to the comfort-focused approach.`
  },
  {
    title: "What Services Does Hospice Provide?",
    content: `Hospice provides comprehensive services including:

Medical Care:
• Regular visits from nurses and doctors
• Pain and symptom management
• Medications related to the terminal illness
• Medical equipment (hospital beds, wheelchairs, oxygen)
• Medical supplies

Support Services:
• Social worker support for emotional and practical needs
• Spiritual care and counseling
• Home health aide assistance with personal care
• Volunteer companionship and respite
• Grief support for family members

All services are coordinated by the hospice team to ensure comprehensive, compassionate care.`
  },
  {
    title: "Where Can Hospice Care Be Provided?",
    content: `Hospice care can be provided in many settings:

• Private homes - The most common location
• Nursing homes or assisted living facilities
• Hospice inpatient facilities - For symptoms that can't be managed at home
• Hospitals - During acute symptom management

The hospice team brings care to wherever the patient is most comfortable. The goal is to allow patients to remain in familiar surroundings with loved ones nearby.`
  },
  {
    title: "How is Hospice Paid For?",
    content: `Hospice care is covered by several sources:

Medicare Hospice Benefit:
• Covers nearly all hospice services
• No copays for most services
• Available to anyone with Medicare Part A

Medicaid:
• Covers hospice in most states
• Similar benefits to Medicare

Private Insurance:
• Most plans cover hospice care
• Coverage varies by plan

No one should avoid hospice due to financial concerns. Most hospice organizations have financial counselors who can help families understand their options.`
  }
];

const benefits = [
  {
    icon: Heart,
    title: "Comfort-Focused Care",
    description: "Expert pain and symptom management to ensure the highest quality of life."
  },
  {
    icon: Home,
    title: "Care at Home",
    description: "Receive care in the comfort of your own home, surrounded by loved ones."
  },
  {
    icon: Users,
    title: "Family Support",
    description: "Comprehensive support for the entire family, including respite care and counseling."
  },
  {
    icon: Pill,
    title: "Medication Coverage",
    description: "Medications related to the terminal diagnosis are typically covered."
  },
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "The hospice team is available around the clock for questions and emergencies."
  },
  {
    icon: CheckCircle,
    title: "Coordinated Care",
    description: "A dedicated team works together to meet all your needs."
  }
];

export default function HospiceEducation() {
  useEffect(() => {
    recordPageVisit("education").catch(console.error);
  }, []);

  return (
    <div className="min-h-screen" data-testid="hospice-education-page">
      {/* Hero */}
      <section className="hero-gradient section-padding pb-16">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="caption mb-4">Understanding Hospice</p>
            <h1 className="heading-1 text-foreground mb-6">
              Hospice Care: Comfort, Dignity & Support
            </h1>
            <p className="body-lg text-muted-foreground mb-8">
              Learn about hospice care - what it is, who it's for, and how it can bring 
              comfort and peace during life's most challenging journey.
            </p>
            <Link to="/chat" data-testid="education-chat-link">
              <Button className="btn-primary">
                <MessageCircle className="w-5 h-5 mr-2" />
                Ask Questions About Hospice
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="section-padding pt-16 bg-white" data-testid="benefits-section">
        <div className="container-max">
          <div className="text-center space-y-4 mb-12">
            <p className="caption">Benefits of Hospice</p>
            <h2 className="heading-2 text-foreground">What Hospice Provides</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="shadow-soft card-hover" data-testid={`benefit-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-muted/30" data-testid="faq-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <p className="caption mb-4">Learn More</p>
              <h2 className="heading-2 text-foreground mb-4">
                Common Questions About Hospice
              </h2>
              <p className="body-base mb-6">
                Understanding hospice care can help you make informed decisions. 
                Here are answers to frequently asked questions.
              </p>
              <img 
                src="https://images.pexels.com/photos/7659565/pexels-photo-7659565.jpeg"
                alt="Medical professional"
                className="rounded-2xl shadow-soft w-full h-48 object-cover hidden lg:block"
              />
            </div>
            
            <div className="lg:col-span-2">
              <Card className="shadow-soft">
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                    {hospiceTopics.map((topic, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left font-serif text-lg hover:text-primary" data-testid={`faq-trigger-${index}`}>
                          {topic.title}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground whitespace-pre-line leading-relaxed" data-testid={`faq-content-${index}`}>
                          {topic.content}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary text-white">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="heading-2 text-white">Have More Questions?</h2>
            <p className="body-lg text-white/80">
              Our AI assistant is here to answer any questions you have about hospice care, 
              eligibility, or how to get started.
            </p>
            <Link to="/chat" data-testid="education-cta-chat">
              <Button variant="secondary" className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg">
                Start a Conversation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
